import { provideHttpClient, withFetch } from '@angular/common/http';
import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  provideClientHydration,
  withIncrementalHydration,
} from '@angular/platform-browser';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { recoverFromChunkLoadError } from '@/app/core/routing/chunk-load-recovery';
import { provideIcons } from '@/app/core/icons/provider';
import { provideTheming } from '@/app/core/theming';
import { provideFirebase, FIREBASE_AUTH, FIREBASE_FUNCTIONS, FIREBASE_STORAGE } from '@/app/core/firebase';
import { provideSentry } from '@/app/core/error-tracking';
import { FORM_BUILDER_AUTH, FORM_BUILDER_FUNCTIONS, FORM_BUILDER_STORAGE } from '@clubezy/form-builder';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withFetch()),
    provideClientHydration(withIncrementalHydration()),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      // A stale bundle asking for a chunk the current deploy no longer has
      // reloads once instead of landing on a blank page (CLUB-2179).
      withNavigationErrorHandler(recoverFromChunkLoadError)
    ),

    // Material
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
    provideNativeDateAdapter(),
    // Dates render/parse as DD/MM/YYYY, not the en-US default MM/DD/YYYY.
    // (CLUB-1780; matches clubezy-administration.) The form renderer overrides
    // the adapter with provideLuxonDateAdapter() at component level, but that
    // provider supplies only DateAdapter + MAT_DATE_FORMATS — LuxonDateAdapter
    // still injects MAT_DATE_LOCALE from here, and MAT_LUXON_DATE_FORMATS'
    // locale-aware 'D' format is what was producing MM/DD/YYYY.
    { provide: MAT_DATE_LOCALE, useValue: 'en-AU' },

    // Core
    provideIcons(),
    provideTheming({
      scheme: 'system',
      // ClubEzy "Court Navy" — anchored at step 600; the tonal engine derives 50–950.
      primary: '#14365C',
      error: '#dc2626',
    }),

    // Firebase
    provideFirebase(),
    // Form-builder file uploads use this app's Firebase Storage, and its Auth
    // for the lazy anonymous sign-in public registrants need to upload at all
    // (CLUB-1941).
    { provide: FORM_BUILDER_STORAGE, useExisting: FIREBASE_STORAGE },
    { provide: FORM_BUILDER_AUTH, useExisting: FIREBASE_AUTH },
    // File-upload fields reserve a slot through a callable (CLUB-2161).
    { provide: FORM_BUILDER_FUNCTIONS, useExisting: FIREBASE_FUNCTIONS },

    // Error Tracking
    provideSentry(),
  ],
};
